import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar, MapPin, Users } from "lucide-react";
import { format, parseISO } from "date-fns";
import { useEffect, useState } from "react";
import client from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { trackEvent, ANALYTICS_EVENTS } from "@/lib/analytics";

export function EventCard({ event }) {
  const { t } = useTranslation();
  const { isAuthenticated, loginWithGoogle } = useAuth();
  const [rsvped, setRsvped] = useState(false);
  const [count, setCount] = useState(event.rsvp_count || 0);

  useEffect(() => {
    if (isAuthenticated) {
      client.get(`/community/events/${event.event_id}/my-rsvp`).then((res) => setRsvped(res.data.rsvped));
    }
  }, [event.event_id, isAuthenticated]);

  const handleRsvp = async () => {
    if (!isAuthenticated) {
      loginWithGoogle("/onboarding");
      return;
    }
    const res = await client.post(`/community/events/${event.event_id}/rsvp`);
    setRsvped(res.data.rsvped);
    setCount(res.data.rsvp_count);
    if (res.data.rsvped) trackEvent(ANALYTICS_EVENTS.EVENT_RSVP, { event_id: event.event_id });
  };

  const date = parseISO(event.date_time);

  return (
    <Card className="rounded-2xl border border-border p-5 flex gap-4" data-testid="events-list">
      <div className="flex h-14 w-14 shrink-0 flex-col items-center justify-center rounded-xl bg-primary/10 text-primary">
        <span className="text-xs font-medium">{format(date, "MMM")}</span>
        <span className="text-lg font-semibold leading-none">{format(date, "d")}</span>
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="rounded-full text-[10px] font-normal">
            {t(`workspace.${event.event_type}`)}
          </Badge>
        </div>
        <h3 className="font-semibold mt-1.5">{event.title}</h3>
        <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{event.description}</p>
        <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {format(date, "MMM d, HH:mm")}</span>
          {event.location && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {event.location}</span>}
          <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {count} {t("workspace.attendees")}</span>
        </div>
      </div>
      <Button
        size="sm"
        variant={rsvped ? "secondary" : "outline"}
        className="rounded-xl self-center shrink-0"
        onClick={handleRsvp}
        data-testid="event-rsvp-button"
      >
        {rsvped ? t("workspace.rsvped") : t("workspace.rsvp")}
      </Button>
    </Card>
  );
}
